/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useState, useCallback } from 'react'
import { UserProviderProps } from '../types/globalTypes'
import ApiService from '../services/ApiService'

export interface Stock {
  id: number
  codeId: number
  material: string
  density: string
  format: string
  finishing: string
  HeatNumber?: any
  fabrication: string
  heatTreatment: string
  originCountry: string
  supplier: string
  stockStreet: string
  shelf: string
  stockLocation: string
  quantity: string
  totalWeight: string
  dimension1: string
  dimension2: string
  lengthInMm: string
  costPerKg: string
  costPerMm: string
  totalCost: string
  code: string
  invoice: any[]
  ncm: string
  certificates: any[]
  receivedDate: string
  totalCmc: string
  markup?: string | number
  trackingNumber: string
  family: string
  description: string
  stockStatus: string
  characteristic: string
  itemColorId: number | null
  color: string
  pantoneCode: string
  pantoneHex: string
  purchaseOrder: string
  measurementUnit: string
  unitOfMeasurementId?: number
  availableBalance: string
  nf: string
  certificateNumber: string
  heatNumber: string
  createdAt: string
  updatedAt: string
  isActivated: boolean
  certificateUrl: string | undefined
  carrierCode?: number | string | null
  isResource?: boolean
  resourceId?: number
  unitQuantity?: string
  stockUnit?: string
  materialId?: number
  prices?: any[]
  formatId?: number
  finishingId?: number
  fabricationId?: number
  heatTreatmentId?: number
  originCountryId?: number
  stockStreetId?: number
  shelfId?: number
  stockLocationId?: number
  ncmId?: number
  familyId?: number
  heatNumberId?: number
  suppliers?: any[]
  certificateId?: number
  invoiceId?: number
  trackingNumberId?: number
  certificateParam?: string
  invoiceParam?: string
  externalCertificateUrl?: string
  externalInvoiceUrl?: string
  attachments?: any[]
  quantities?: any[]
}

interface StockContextType {
  stock: Stock
  setStock: React.Dispatch<React.SetStateAction<Stock>>
  resetContext: () => void
  stockComponent: string
  setStockComponent: React.Dispatch<React.SetStateAction<string>>
  importFromExcelModal: boolean
  setImportFromExcelModal: React.Dispatch<React.SetStateAction<boolean>>
  tableFile: any
  setTableFile: React.Dispatch<React.SetStateAction<any>>
  stockQuantity: number
  setStockQuantity: React.Dispatch<React.SetStateAction<number>>
  movementQuantity: number
  setMovementQuantity: React.Dispatch<React.SetStateAction<number>>
  details: boolean
  setDetails: React.Dispatch<React.SetStateAction<boolean>>
  stockList: Stock[]
  setStockList: React.Dispatch<React.SetStateAction<Stock[]>>
  updateStockList: (
    quantity?: number,
    filters?: any,
    search?: string,
    refresh?: any,
  ) => void
  refresh: boolean
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>
  hasMoreStock: boolean
  isLoading: boolean
}

const initialStockState: Stock = {
  id: 0,
  codeId: 0,
  material: '',
  density: '',
  format: '',
  finishing: '',
  fabrication: '',
  heatTreatment: '',
  originCountry: '',
  supplier: '',
  stockStreet: '',
  shelf: '',
  stockLocation: '',
  quantity: '',
  totalWeight: '',
  dimension1: '',
  dimension2: '',
  lengthInMm: '',
  costPerKg: '',
  costPerMm: '',
  totalCost: '',
  code: '',
  invoice: [],
  ncm: '',
  certificates: [],
  receivedDate: '',
  totalCmc: '',
  markup: '',
  trackingNumber: '',
  family: '',
  description: '',
  stockStatus: '',
  characteristic: '',
  itemColorId: null,
  color: '',
  pantoneCode: '',
  pantoneHex: '',
  purchaseOrder: '',
  measurementUnit: 'KG',
  availableBalance: '0',
  nf: '',
  certificateNumber: '',
  heatNumber: '',
  isActivated: true,
  createdAt: '',
  updatedAt: '',
  certificateUrl: '',
  materialId: undefined,
  formatId: undefined,
  finishingId: undefined,
  fabricationId: undefined,
  heatTreatmentId: undefined,
  originCountryId: undefined,
  stockStreetId: undefined,
  shelfId: undefined,
  stockLocationId: undefined,
  ncmId: undefined,
  familyId: undefined,
  heatNumberId: undefined,
  suppliers: [],
  prices: [],
}

export const StockContext = createContext<StockContextType>({
  stock: initialStockState,
  setStock: () => { },
  resetContext: () => { },
  stockComponent: '',
  setStockComponent: () => { },
  importFromExcelModal: false,
  setImportFromExcelModal: () => { },
  tableFile: '',
  setTableFile: () => { },
  stockQuantity: 10,
  setStockQuantity: () => { },
  movementQuantity: 10,
  setMovementQuantity: () => { },
  details: false,
  setDetails: () => { },
  stockList: [],
  setStockList: () => { },
  updateStockList: () => { },
  refresh: false,
  setRefresh: () => { },
  hasMoreStock: true,
  isLoading: false,
})

export const StockProvider: React.FC<any> = ({ children }) => {
  const [stock, setStock] = useState<Stock>(initialStockState)
  const [stockComponent, setStockComponent] = useState<string>('table')
  const [importFromExcelModal, setImportFromExcelModal] =
    useState<boolean>(false)
  const [tableFile, setTableFile] = useState<any>()
  const [stockQuantity, setStockQuantity] = useState<number>(10)
  const [movementQuantity, setMovementQuantity] = useState<number>(10)
  const [details, setDetails] = useState<boolean>(false)
  const [stockList, setStockList] = useState<Stock[]>([])
  const [refresh, setRefresh] = useState<boolean>(false)
  const [hasMoreStock, setHasMoreStock] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const resetContext = () => {
    setStock(initialStockState)
  }

  const updateStockList = useCallback(async (quantity = 10, filters = [], search = '') => {
    setIsLoading(true)

    const parameters: any = { size: quantity }
    if (filters && Array.isArray(filters) && filters.length > 0) parameters.filters = JSON.stringify(filters)
    if (search && search.trim().length > 0) parameters.global = search

    console.log('📦 [STOCK] Buscando com params:', parameters)

    try {
      const response = await ApiService.stock.findAll(parameters)
      if (response?.data) {
        const items = Array.isArray(response.data) ? response.data : response.data.items || []
        console.log('📦 [STOCK] Retornou:', items.length, 'itens. Esperava:', quantity)
        setStockList(items)
        setHasMoreStock(items.length === quantity)
      }
    } catch (error: any) {
      console.error('Ocorreu um erro ao obter os dados do estoque', error)

      if (error.response?.status === 401) {
        localStorage.removeItem('token')
        window.location.href = '/'
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  return (
    <StockContext.Provider
      value={{
        stock,
        setStock,
        resetContext,
        stockComponent,
        setStockComponent,
        importFromExcelModal,
        setImportFromExcelModal,
        tableFile,
        setTableFile,
        stockQuantity,
        setStockQuantity,
        movementQuantity,
        setMovementQuantity,
        details,
        setDetails,
        stockList,
        setStockList,
        updateStockList,
        refresh,
        setRefresh,
        hasMoreStock,
        isLoading,
      }}
    >
      {children}
    </StockContext.Provider>
  )
}

export default StockContext
